import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle,
  MessageSquare
} from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    projectType: '',
    message: '',
    budget: '',
    timeline: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Ici, vous ajouteriez la logique d'envoi du formulaire
    console.log('Form data:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        projectType: '',
        message: '',
        budget: '',
        timeline: ''
      });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Téléphone',
      content: '+33 6 76 82 52 28',
      action: 'tel:+33676825228',
      description: 'Du lundi au vendredi, 8h-18h'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contact@ars-agencement.bzh',
      action: 'mailto:contact@ars-agencement.bzh',
      description: 'Réponse sous 24h'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      content: '90 Rue du Clos Tilhen\n56000 VANNES',
      action: null,
      description: 'Zone d\'intervention : Morbihan'
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun-Ven: 8h-18h\nSam: 9h-12h',
      action: null,
      description: 'Sur rendez-vous'
    }
  ];

  const projectTypes = [
    'Agencement intérieur',
    'Agencement extérieur',
    'Menuiseries',
    'Terrasse/Pergola',
    'Cuisine sur mesure',
    'Dressing/Placard',
    'Rénovation complète',
    'Autre'
  ];

  const budgetRanges = [
    'Moins de 5 000€',
    '5 000€ - 15 000€',
    '15 000€ - 30 000€',
    '30 000€ - 50 000€',
    'Plus de 50 000€',
    'À définir'
  ];

  return (
    <section id="contact" className="py-20 bg-stone-50">
      <div className="container-max section-padding">
        {/* En-tête */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            Contactez-<span className="text-wood-600">nous</span>
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Prêt à donner vie à votre projet ? Contactez-nous pour un devis gratuit 
            et personnalisé. Nous vous accompagnons de A à Z dans votre démarche.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Informations de contact */}
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-stone-900 mb-6">
                Nos coordonnées
              </h3>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-wood-100 rounded-lg flex items-center justify-center">
                      <info.icon className="w-6 h-6 text-wood-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-stone-900 mb-1">{info.title}</h4>
                      {info.action ? (
                        <a 
                          href={info.action}
                          className="text-wood-600 hover:text-wood-700 font-medium whitespace-pre-line"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <div className="text-stone-700 whitespace-pre-line">{info.content}</div>
                      )}
                      <p className="text-sm text-stone-500 mt-1">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Zone d'intervention */}
            <div className="bg-white p-6 rounded-lg border border-stone-200">
              <h4 className="font-bold text-stone-900 mb-4 flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-wood-600" />
                <span>Zone d'intervention</span>
              </h4>
              <div className="space-y-2 text-stone-600">
                <p><strong>Morbihan (56)</strong></p>
                <p>• Focus sur Vannes et le Golfe du Morbihan</p>
                <p>• Déplacements possibles dans tout le département</p>
                <p>• Devis de déplacement pour autres zones</p>
              </div>
            </div>

            {/* Urgences */}
            <div className="bg-wood-50 p-6 rounded-lg border border-wood-200">
              <h4 className="font-bold text-stone-900 mb-4 flex items-center space-x-2">
                <Phone className="w-5 h-5 text-wood-600" />
                <span>Urgences</span>
              </h4>
              <p className="text-stone-600 mb-3">
                Pour les interventions urgentes (sécurisation, réparations d'urgence)
              </p>
              <a 
                href="tel:+33676825228"
                className="bg-wood-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-wood-700 transition-colors inline-block"
              >
                Appeler maintenant
              </a>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-lg shadow-lg border border-stone-200">
              <h3 className="text-2xl font-bold text-stone-900 mb-6 flex items-center space-x-2">
                <MessageSquare className="w-6 h-6 text-wood-600" />
                <span>Demande de devis gratuit</span>
              </h3>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h4 className="text-xl font-bold text-stone-900 mb-2">Message envoyé !</h4>
                  <p className="text-stone-600">
                    Merci pour votre demande. Nous vous recontacterons dans les plus brefs délais.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Informations personnelles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                        placeholder="Votre nom et prénom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                        placeholder="+33 6 76 82 52 28"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Type de projet *
                      </label>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez un type</option>
                        {projectTypes.map((type, index) => (
                          <option key={index} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Sujet de votre demande *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                      placeholder="Ex: Aménagement cuisine 20m²"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Budget prévisionnel
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez une fourchette</option>
                        {budgetRanges.map((range, index) => (
                          <option key={index} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Délai souhaité
                      </label>
                      <select
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent"
                      >
                        <option value="">Sélectionnez un délai</option>
                        <option value="Urgent (moins de 1 mois)">Urgent (moins de 1 mois)</option>
                        <option value="1-3 mois">1-3 mois</option>
                        <option value="3-6 mois">3-6 mois</option>
                        <option value="6-12 mois">6-12 mois</option>
                        <option value="Pas de contrainte">Pas de contrainte de délai</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Description de votre projet *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-wood-500 focus:border-transparent resize-none"
                      placeholder="Décrivez votre projet en détail : dimensions, matériaux souhaités, contraintes particulières, inspiration..."
                    ></textarea>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="consent"
                      required
                      className="w-4 h-4 text-wood-600 border-stone-300 rounded focus:ring-wood-500"
                    />
                    <label htmlFor="consent" className="text-sm text-stone-600">
                      J'accepte que mes données soient utilisées pour me recontacter concernant ma demande *
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-wood-600 hover:bg-wood-700 text-white px-8 py-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <Send className="w-5 h-5" />
                    <span>Envoyer ma demande</span>
                  </button>

                  <p className="text-sm text-stone-500 text-center">
                    * Champs obligatoires - Devis gratuit et sans engagement
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-16">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-stone-200">
            <div className="p-4 bg-stone-50 border-b border-stone-200">
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-wood-600" />
                <div>
                  <h3 className="font-semibold text-stone-900">Notre atelier</h3>
                  <p className="text-sm text-stone-600">90 Rue du Clos Tilhen, 56000 VANNES</p>
                </div>
              </div>
            </div>
            <div className="relative h-80">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2723.742639!2d-2.7628139!3d47.6584158!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDfCsDM5JzMwLjMiTiAyw7A0NScxOC4xIlc!5e0!3m2!1sfr!2sfr!4v1697112000000!5m2!1sfr!2sfr"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ARS Agencement - Localisation Vannes"
                className="w-full h-full"
              />
              
              {/* Overlay informatif */}
              <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg border border-stone-200 max-w-xs">
                <div className="text-sm">
                  <div className="font-semibold text-stone-900 mb-1">ARS Agencement</div>
                  <div className="text-stone-600 mb-2">Artisan Menuisier</div>
                  <div className="space-y-1 text-xs text-stone-500">
                    <div>📍 90 Rue du Clos Tilhen</div>
                    <div>🏙️ 56000 VANNES</div>
                    <div>📞 +33 6 76 82 52 28</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Actions */}
            <div className="p-4 bg-stone-50 flex flex-col sm:flex-row gap-3">
              <a
                href="https://maps.google.com/?q=90+Rue+du+Clos+Tilhen,+56000+VANNES"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-wood-600 hover:bg-wood-700 text-white px-4 py-2 rounded-lg font-medium text-center transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <MapPin className="w-4 h-4" />
                <span>Ouvrir dans Google Maps</span>
              </a>
              <a
                href="https://maps.google.com/maps/dir//90+Rue+du+Clos+Tilhen,+56000+VANNES"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-sage-100 hover:bg-sage-200 text-sage-800 px-4 py-2 rounded-lg font-medium text-center transition-colors duration-200"
              >
                Itinéraire
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
